import React, { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { authActions } from "../../../../context/actions/authActions";
import useStore from "../../../../context/useStore";
import ROLES_MANAGER_PAR_DEFAUT from "../../../../helpers/rolesManager";
import sidenavItemsParDefaut from "../../../../sidenavItems";
import roleConfig from "../roleConfig";
import RechercheRapide from "../../RechercheRapide/RechercheRapide";
import { peutRechercherAllocataires } from "../../RechercheRapide/saisieEtSuggestions";
import BarreHauteMobile from "./BarreHauteMobile";
import CompteMenu from "./CompteMenu";
import ElementMenu from "./ElementMenu";
import GroupeMenu from "./GroupeMenu";
import "./menu-lateral.scss";
import { BadgeEnvironnement, badgeEnvironnement } from "./environnement";
import useMenuLateral from "./useMenuLateral";
import { contientRoute, entreeActive, estGroupe, filtrerMenu, privilegesMasques } from "./visibiliteMenu";

export const CLASSE_TIROIR = "coquille-tiroir";

// Libellé du rôle : constantes de l'API si elles servent ROLES_MANAGER, sinon
// le miroir local ; rôle inconnu → « SUPER ADMIN » (repli historique de la
// barre haute).
export function libelleDuRole(user, constantes) {
  const roles = constantes && Array.isArray(constantes.ROLES_MANAGER) ? constantes.ROLES_MANAGER : ROLES_MANAGER_PAR_DEFAUT;
  const role = roles.find((r) => r.id === user.role);
  return (role && role.name) || "SUPER ADMIN";
}

// Coquille connectée : menu latéral (rail / épinglé / tiroir) avec les mêmes
// entrées, le même ordre et les mêmes groupes que sidenavItems, filtrés par
// rôle, plus le compte en pied. Rendu vide hors connexion ou quand le mot de
// passe doit être renouvelé, comme l'ancienne Sidenav.
const MenuLateral = ({ items = sidenavItemsParDefaut, environnement = process.env.REACT_APP_NODE_ENV }) => {
  const [state, dispatch] = useStore();
  const { isAuthenticated, user } = state.auth;
  const { pathname } = useLocation();
  const visibles = useMemo(() => filtrerMenu(items, user, privilegesMasques(user, roleConfig)), [items, user]);
  const menu = useMenuLateral(visibles);
  const affiche = Boolean(isAuthenticated && !user.mustRenewPassword);
  const badge = useMemo(() => badgeEnvironnement(environnement), [environnement]);
  // Recherche rapide d'un allocataire : pour les rôles qui voient l'entrée
  // « Rechercher un allocataire ».
  const recherche = peutRechercherAllocataires(visibles);

  const refOuvrirTiroir = useRef(null);
  const refFermerTiroir = useRef(null);
  const tiroirPrecedent = useRef(false);

  // La grille de la coquille (App.js) suit le mode via une classe sur <body>.
  useEffect(() => {
    document.body.classList.toggle(CLASSE_TIROIR, affiche && menu.mobile);
    return () => document.body.classList.remove(CLASSE_TIROIR);
  }, [affiche, menu.mobile]);

  // Tiroir : le focus entre dans le menu à l'ouverture et revient au bouton
  // de la barre haute à la fermeture.
  useEffect(() => {
    if (menu.mode !== "tiroir") {
      tiroirPrecedent.current = false;
      return;
    }
    if (menu.tiroirOuvert && refFermerTiroir.current) refFermerTiroir.current.focus();
    else if (!menu.tiroirOuvert && tiroirPrecedent.current && refOuvrirTiroir.current) refOuvrirTiroir.current.focus();
    tiroirPrecedent.current = menu.tiroirOuvert;
  }, [menu.tiroirOuvert, menu.mode]);

  if (!affiche) return null;

  const rail = menu.mode === "rail";
  const classes = [
    "menu-lateral",
    `menu-lateral--${menu.mode}`,
    menu.tiroirOuvert ? "menu-lateral--ouvert" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const courante = entreeActive(visibles, pathname);
  const cheminActif = courante ? courante.path : null;

  // Un filet sépare les entrées simples des groupes, où qu'ils se suivent.
  const lignes = [];
  visibles.forEach((item, index) => {
    const precedent = visibles[index - 1];
    if (precedent && estGroupe(precedent) !== estGroupe(item)) {
      lignes.push(<li key={`separateur-${index}`} className="menu-lateral__separateur" role="presentation" />);
    }
    lignes.push(
      estGroupe(item) ? (
        <GroupeMenu
          key={item.name}
          groupe={item}
          mode={menu.mode}
          ouvert={menu.groupeOuvert === item.name}
          contientActif={contientRoute(item, pathname)}
          cheminActif={cheminActif}
          onBasculer={menu.basculerGroupe}
          onSurvoler={menu.survolerGroupe}
          onQuitter={menu.quitterGroupe}
        />
      ) : (
        <ElementMenu key={item.path} item={item} infobulle={rail} cheminActif={cheminActif} />
      )
    );
  });

  return (
    <>
      {menu.mobile && (
        <BarreHauteMobile
          ouvert={menu.tiroirOuvert}
          onOuvrir={menu.ouvrirTiroir}
          titre={courante ? courante.name : ""}
          badge={badge}
          refBouton={refOuvrirTiroir}
          recherche={recherche ? <RechercheRapide variante="tiroir" /> : null}
        />
      )}
      {menu.mobile && menu.tiroirOuvert && <div className="menu-lateral__voile" onClick={menu.fermerTiroir} />}
      <nav id="menu-lateral" className={classes} aria-label="Navigation principale" ref={menu.refNav}>
        <div className="menu-lateral__tete">
          {!rail && (
            <Link to="/" className="menu-lateral__accueil">
              <img className="menu-lateral__logo" src="/assets/logo-espoar-new.png" alt="ESPOAR — accueil" />
              <BadgeEnvironnement badge={badge} />
            </Link>
          )}
          {menu.mode === "tiroir" ? (
            <button
              ref={refFermerTiroir}
              type="button"
              className="menu-lateral__bouton"
              aria-label="Fermer le menu"
              onClick={menu.fermerTiroir}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="menu-lateral__bouton"
              aria-pressed={menu.epingle}
              aria-label={menu.epingle ? "Réduire le menu" : "Déplier le menu"}
              title={menu.epingle ? "Réduire le menu" : "Déplier le menu"}
              onClick={menu.basculerEpingle}
            >
              <i className="fa fa-bars" aria-hidden="true" />
            </button>
          )}
        </div>
        {rail && <BadgeEnvironnement badge={badge} abrege className="badge-env--rail" />}
        {recherche && menu.mode !== "tiroir" && (
          <div className="menu-lateral__recherche">
            <RechercheRapide variante={menu.mode} />
          </div>
        )}
        <div className="menu-lateral__liste">
          <ul>{lignes}</ul>
        </div>
        <CompteMenu
          user={user}
          libelleRole={libelleDuRole(user, state.constants.items)}
          ouvert={menu.compteOuvert}
          onBasculer={menu.basculerCompte}
          onDeconnexion={() => authActions(state, dispatch).logoutUser()}
        />
      </nav>
    </>
  );
};

export default MenuLateral;
